import React, { useEffect, useState } from "react";
import { Offcanvas, Spinner } from "react-bootstrap";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import Request from "../../../../helpers/request";
import * as url from "../../../../helpers/url_helper";
import { Tooltip } from "react-tooltip";

interface AccountSummaryProps {
  userId?: string;
  onPendingActionsChange?: (actions: SummaryData["pendingActions"]) => void;
}

type Status = "COMPLETED" | "PARTIAL" | string;

interface ChecklistItem {
  completed: number;
  remaining: number;
  status: Status;
  [key: string]: any;
}

interface SummaryData {
  premiseId: string;
  accountSetup: { completionPercentage: number; remainingPercentage: number };
  deviceSummary: {
    total: number;
    sceneScheduleRequiredDevices: number;
    chips: {
      total: number;
      sceneConfigured: number;
      scenePending: number;
      scheduleConfigured: number;
      schedulePending: number;
    };
    airDriver: {
      total: number;
      driver: {
        sceneConfigured: number;
        scenePending: number;
        scheduleConfigured: number;
        schedulePending: number;
      };
      group: { total: number; requiredGroups: number };
    };
    airCurtain: {
      total: number;
      sceneConfigured: number;
      scenePending: number;
      scheduleConfigured: number;
      schedulePending: number;
    };
    blaster: { total: number };
    lock: { total: number; added: boolean };
  };
  setupChecklist: {
    device: ChecklistItem & {
      airDriverGroup: {
        requiredGroups: number;
        totalGroups: number;
        pendingGroups: number;
        completionPercentage: number;
      };
    };
    scene: ChecklistItem & { totalScenes: number; requiredScenes: number };
    schedule: ChecklistItem & {
      totalSchedules: number;
      requiredSchedules: number;
    };
    naming: ChecklistItem & {
      totalSwitches: number;
      namedSwitches: number;
      pendingSwitches: number;
    };
  };
  pendingActions: {
    type: string;
    remainingPercentage: number;
    message: string;
  }[];
}

const C = {
  green: "#198754",
  greenSoft: "#e8f5ee",
  greenText: "#0f7a4a",
  red: "#dc3545",
  redSoft: "#fdecee",
  blue: "#0d6efd",
  blueSoft: "#e7f0ff",
  orange: "#fd7e14",
  orangeSoft: "#fff0e2",
  amber: "#b7791f",
  amberSoft: "#fff4d6",
  dark: "#1f2937",
  muted: "#6c757d",
  border: "#e9ecef",
  panel: "#f3f6fa",
  track: "#e9edf3",
};

type Tone = "green" | "blue" | "purple" | "orange" | "red";

const TONES: Record<
  Tone,
  { bg: string; border: string; text: string; badge: string }
> = {
  green: {
    bg: "#effaf3",
    border: "#cdeedb",
    text: "#16a34a",
    badge: "#d5f5e3",
  },
  blue: { bg: "#eef4ff", border: "#d0defc", text: "#1d4ed8", badge: "#dbe7ff" },
  purple: {
    bg: "#f3f0ff",
    border: "#ddd6fb",
    text: "#6d28d9",
    badge: "#e4defc",
  },
  orange: {
    bg: "#fff4ec",
    border: "#fde0cb",
    text: "#ea580c",
    badge: "#ffe1cc",
  },
  red: { bg: "#fdecec", border: "#f9cfcf", text: "#dc2626", badge: "#fdd9d9" },
};

const cardStyle: React.CSSProperties = {
  border: `1px solid ${C.border}`,
  borderRadius: 10,
};

/* ---------- small building blocks ---------- */

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const done = status === "COMPLETED";
  return (
    <span
      className="badge rounded-pill flex-shrink-0"
      style={{
        background: done ? C.greenSoft : C.amberSoft,
        color: done ? C.greenText : C.amber,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.4,
        padding: "4px 10px",
      }}
    >
      {status}
    </span>
  );
};

const StatCell: React.FC<{
  value: number | string;
  label: string;
  color?: string;
  last?: boolean;
}> = ({ value, label, color, last }) => (
  <div
    className="flex-fill px-2"
    style={{ borderRight: last ? "none" : `1px solid ${C.border}` }}
  >
    <div
      className="fw-bold lh-sm"
      style={{ color: color ?? C.dark, fontSize: 14 }}
    >
      {value}
    </div>
    <div style={{ color: C.muted, fontSize: 11 }}>{label}</div>
  </div>
);

const StatsBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="d-flex py-1" style={{ background: C.panel, borderRadius: 8 }}>
    {children}
  </div>
);

const Pill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    className="fw-semibold px-2 py-1 text-nowrap"
    style={{
      background: "#eef2f7",
      borderRadius: 20,
      fontSize: 12,
      color: C.dark,
    }}
  >
    {children}
  </span>
);

const CardHeader: React.FC<{ title: string; subtitle: string }> = ({
  title,
  subtitle,
}) => (
  <div className="pb-2 mb-2" style={{ borderBottom: `1px solid ${C.border}` }}>
    <div className="fw-bold fs-5 lh-sm" style={{ color: C.dark }}>
      {title}
    </div>
    <div className="text-secondary" style={{ fontSize: 12 }}>
      {subtitle}
    </div>
  </div>
);

/* ---------- Top summary cards ---------- */
const SummaryCard: React.FC<{
  tone: Tone;
  value: number | string;
  label: string;
  sub?: string;
}> = ({ tone, value, label, sub }) => (
  <div
    className="d-flex align-items-center justify-content-center px-2 py-2 h-100 text-center"
    style={{
      background: TONES[tone].bg,
      border: `1px solid ${TONES[tone].border}`,
      borderRadius: 10,
    }}
  >
    <div>
      <div className="fw-bold fs-4 lh-1" style={{ color: TONES[tone].text }}>
        {value}
      </div>
      <div className="mt-1 lh-sm" style={{ color: C.dark, fontSize: 12 }}>
        {label}
      </div>
      {sub && (
        <div className="text-secondary lh-sm" style={{ fontSize: 10 }}>
          {sub}
        </div>
      )}
    </div>
  </div>
);

/* ---------- Scene / Schedule / Groups box ---------- */
type BoxItem = {
  value: number | string;
  label: string;
  sub?: string;
  color: "green" | "red" | "blue";
};

type Box = { title: string; tone: Tone; items: BoxItem[] };

const ConfigBox: React.FC<Box> = ({ title, tone, items }) => (
  <div
    className="px-2 py-2 h-100"
    style={{
      background: TONES[tone].bg,
      border: `1px solid ${TONES[tone].border}`,
      borderRadius: 8,
    }}
  >
    <div className="fw-semibold mb-1" style={{ color: C.dark, fontSize: 12 }}>
      {title}
    </div>
    <div className="d-flex align-items-center">
      {items.map((it) => (
        <div
          key={it.label}
          className="d-flex align-items-center gap-1 flex-fill"
        >
          <span
            className="d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
            style={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              background: TONES[it.color].badge,
              color: TONES[it.color].text,
              fontSize: 13,
            }}
          >
            {it.value}
          </span>
          <div className="lh-sm" style={{ color: C.dark, fontSize: 11 }}>
            {it.label}
            {it.sub && <div className="text-secondary">{it.sub}</div>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ---------- Device row card ---------- */
const DeviceRow: React.FC<{
  title: string;
  subtitle: string;
  total: number;
  boxes?: Box[];
  note?: string;
}> = ({ title, subtitle, total, boxes, note }) => (
  <div className="bg-white p-2 h-100" style={cardStyle}>
    <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
      <div>
        <span className="fw-bold" style={{ color: C.dark, fontSize: 14 }}>
          {title}
        </span>
        <span className="text-secondary ms-2" style={{ fontSize: 11 }}>
          {subtitle}
        </span>
      </div>
      <Pill>Total: {total}</Pill>
    </div>

    {boxes && (
      <div className="row g-2">
        {boxes.map((b) => (
          <div key={b.title} className="col">
            <ConfigBox {...b} />
          </div>
        ))}
      </div>
    )}

    {note && (
      <div
        className="px-2 py-1"
        style={{
          background: TONES.orange.bg,
          color: "#7c4a1d",
          borderRadius: 6,
          fontSize: 11,
        }}
      >
        {note}
      </div>
    )}
  </div>
);

/* ---------- Checklist card ---------- */
const ChecklistCard: React.FC<{
  title: string;
  subtitle: string;
  item: ChecklistItem;
  statsTitle?: string;
  stats: { value: number | string; label: string; color?: string }[];
}> = ({ title, subtitle, item, statsTitle, stats }) => {
  const done = item.status === "COMPLETED";
  const barColor = done ? C.green : C.blue;
  const pct = Math.min(100, Math.max(0, item.completed));

  return (
    <div className="bg-white p-3 d-flex flex-column h-100" style={cardStyle}>
      {/* title + status */}
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div>
          <div
            className="fw-semibold lh-sm"
            style={{ color: C.dark, fontSize: 14 }}
          >
            {title}
          </div>
          <div style={{ color: C.muted, fontSize: 11 }}>{subtitle}</div>
        </div>
        <StatusPill status={item.status} />
      </div>

      {/* progress */}
      <div className="d-flex align-items-center gap-2 mt-3">
        <div
          className="progress flex-grow-1"
          style={{ height: 6, background: C.track, borderRadius: 999 }}
        >
          <div
            className="progress-bar"
            role="progressbar"
            style={{
              width: `${pct}%`,
              background: barColor,
              borderRadius: 999,
            }}
          />
        </div>
        <span
          className="fw-bold text-end"
          style={{ color: barColor, minWidth: 48, fontSize: 13 }}
        >
          {+pct.toFixed(2)}%
        </span>
      </div>

      {/* stats pinned to the bottom so all cards line up */}
      <div className="mt-auto pt-3">
        {statsTitle && (
          <div
            className="fw-semibold mb-1"
            style={{ color: C.greenText, fontSize: 11 }}
          >
            {statsTitle}
          </div>
        )}
        <StatsBox>
          {stats.map((s, i) => (
            <StatCell
              key={s.label}
              value={s.value}
              label={s.label}
              color={s.color}
              last={i === stats.length - 1}
            />
          ))}
        </StatsBox>
      </div>
    </div>
  );
};

const AccountSummary: React.FC<AccountSummaryProps> = ({ userId , onPendingActionsChange }) => {
  const [show, setShow] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [data, setData] = useState<SummaryData | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [arrowMove, setArrowMove] = useState(false);

  const handleOpen = () => setShow(true);
  const handleClose = () => setShow(false);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        if (show) setLoading(true);
        setError("");

        const res: any = await Request.post(url.ACCOUNT_SUMMERY, { userId });
        const json = res?.data ?? res;
        if (!json.success) throw new Error(json.message);

        setData(json.data);
        onPendingActionsChange?.(json.data?.pendingActions ?? []);
      } catch (e: any) {
        setError(e?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [show, userId]);

  useEffect(() => {
    const interval = setInterval(() => setArrowMove((p) => !p), 600);
    return () => clearInterval(interval);
  }, []);

  const copyPremiseId = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.premiseId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  /* Big chart (inside the panel) */
  const radialOptions: ApexOptions = {
    chart: { type: "radialBar", sparkline: { enabled: true } },
    plotOptions: {
      radialBar: {
        startAngle: 0,
        endAngle: 360,
        hollow: { size: "68%" },
        track: { background: "#e9ecef", strokeWidth: "100%" },
        dataLabels: {
          name: { show: true, offsetY: 16, color: C.muted, fontSize: "11px" },
          value: {
            show: true,
            offsetY: -14,
            fontSize: "18px",
            fontWeight: 700,
            color: C.dark,
            formatter: (v: number) => `${v}%`,
          },
        },
      },
    },
    labels: ["Completed"],
    colors: [C.green],
    stroke: { lineCap: "round" },
  };

  /* Small chart (header icon) */
  const miniRadialOptions: ApexOptions = {
    chart: { type: "radialBar", sparkline: { enabled: true } },
    plotOptions: {
      radialBar: {
        startAngle: 0,
        endAngle: 360,
        hollow: { size: "60%" },
        track: { background: "#e9ecef", strokeWidth: "100%", margin: 0 },
        dataLabels: {
          name: { show: false },
          value: {
            show: true,
            offsetY: 4,
            fontSize: "12px",
            fontWeight: 700,
            color: C.dark,
            formatter: (v: number) => `${Math.round(v)}`,
          },
        },
      },
    },
    colors: [C.green],
    stroke: { lineCap: "round" },
    tooltip: { enabled: false },
  };

  const pendingTitle: Record<string, string> = {
    SCHEDULE: "Create more schedules",
    NAMING: "Name your switches",
    SCENE: "Create more scenes",
    DEVICE: "Add required devices",
    AIR_DRIVER_GROUP: "Add Air Driver groups",
  };

  const d = data?.deviceSummary;
  const otherDevices = d ? d.blaster.total + d.lock.total : 0;
  const progress = data?.accountSetup?.completionPercentage ?? 0;

  const setupStatus =
    progress >= 100
      ? {
          title: "Setup Complete!",
          message: "All setup items are complete.",
          icon: "bx bx-check",
          color: C.green,
          bg: C.greenSoft,
        }
      : progress >= 80
      ? {
          title: "Almost There!",
          message: "Complete the remaining items to finish your setup.",
          icon: "bx bx-rocket",
          color: C.blue,
          bg: C.blueSoft,
        }
      : progress >= 25
      ? {
          title: "Good Progress!",
          message: "Keep completing the setup items to reach 100%.",
          icon: "bx bx-loader-circle",
          color: C.orange,
          bg: C.orangeSoft,
        }
      : {
          title: "Let's Get Started!",
          message:
            "Start completing the setup items to get your premise ready.",
          icon: "bx bx-play-circle",
          color: C.red,
          bg: C.redSoft,
        };

  /* helper to build Scene + Schedule boxes for a device group */
  const sceneScheduleBoxes = (x: {
    sceneConfigured: number;
    scenePending: number;
    scheduleConfigured: number;
    schedulePending: number;
  }): Box[] => [
    {
      title: "Scene",
      tone: "green",
      items: [
        { value: x.sceneConfigured, label: "Configured", color: "green" },
        { value: x.scenePending, label: "Pending", color: "red" },
      ],
    },
    {
      title: "Schedule",
      tone: "blue",
      items: [
        { value: x.scheduleConfigured, label: "Configured", color: "green" },
        { value: x.schedulePending, label: "Pending", color: "red" },
      ],
    },
  ];

  return (
    <>
      <div
        onClick={handleOpen}
        className="d-flex align-items-center"
        style={{ cursor: "pointer", marginRight: "12px" }}
      >
        <div
          id="account-summary"
          className="d-flex align-items-center"
          style={{ width: 44, height: 44 }}
        >
          {data ? (
            <div style={{ pointerEvents: "none" }}>
              <Chart
                type="radialBar"
                width={44}
                height={44}
                series={[data.accountSetup.completionPercentage]}
                options={miniRadialOptions}
              />
            </div>
          ) : (
            <i
              className="fa-solid fa-border-none"
              style={{ fontSize: 26, color: C.dark }}
            />
          )}
        </div>

        {/* animated >> icon */}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={C.dark}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: `translateX(${arrowMove ? 5 : 0}px)`,
            transition: "transform 0.3s ease",
          }}
        >
          <polyline points="5 5 12 12 5 19" />
          <polyline points="13 5 20 12 13 19" />
        </svg>

        <Tooltip
          anchorSelect="#account-summary"
          place="top"
          content="Account Summary"
        />
      </div>

      <Offcanvas
        show={show}
        onHide={handleClose}
        placement="end"
        style={{ width: "95%" }}
        className="border-start"
      >
        <Offcanvas.Header
          closeButton
          className="d-flex align-items-center justify-content-between"
        >
          <Offcanvas.Title className="fs-4">Account Summary</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className="p-3" style={{ background: "#f6f8fb" }}>
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          )}

          {error && !loading && (
            <div className="alert alert-danger">{error}</div>
          )}

          {data && d && !loading && (
            <>
              {/* ===== Header ===== */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                  <h5 className="mb-0 fw-bold" style={{ color: C.dark }}>
                    Premise Setup Overview
                  </h5>
                  <div className="text-secondary small">
                    Track your device installation, scene, schedule and naming
                    setup progress
                  </div>
                </div>

                <div
                  className="d-flex align-items-center gap-2 bg-white px-2 py-1"
                  style={cardStyle}
                >
                  <div>
                    <div className="text-secondary" style={{ fontSize: 10 }}>
                      Premise ID
                    </div>
                    <div className="small fw-medium">{data.premiseId}</div>
                  </div>
                  <button
                    type="button"
                    onClick={copyPremiseId}
                    className="btn btn-sm btn-light"
                    title="Copy premise ID"
                    style={{ width: 30, height: 28, padding: 0 }}
                  >
                    {copied ? "✔" : <i className="bx bx-copy" />}
                  </button>
                </div>
              </div>

              {/* ===== Row 1: Account Setup + Pending Actions ===== */}
              <div className="row g-3 mb-3">
                <div className="col-8 col-xl-6">
                  <div
                    className="bg-white p-3 h-100 d-flex align-items-center gap-4"
                    style={cardStyle}
                  >
                    {/* 1. Chart */}
                    <div style={{ width: 240, height: 220, flexShrink: 0 }}>
                      <Chart
                        type="radialBar"
                        width={240}
                        height={240}
                        series={[data.accountSetup.completionPercentage]}
                        options={radialOptions}
                      />
                    </div>

                    {/* 2. Numbers (takes the free space) */}
                    <div className="flex-grow-1" style={{ minWidth: 100 }}>
                      <div
                        className="fw-bold fs-5 lh-sm"
                        style={{ color: C.dark }}
                      >
                        Account Setup
                      </div>
                      <div className="text-secondary small mb-3">
                        Overall progress of your premise setup
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <div>
                          <div
                            className="fw-bold fs-3 lh-1"
                            style={{ color: C.green }}
                          >
                            {data.accountSetup.completionPercentage}%
                          </div>
                          <div
                            className="small mt-1"
                            style={{ color: C.green }}
                          >
                            Completed
                          </div>
                        </div>
                        <div
                          style={{ width: 1, height: 44, background: C.border }}
                        />
                        <div>
                          <div
                            className="fw-bold fs-3 lh-1"
                            style={{ color: C.red }}
                          >
                            {data.accountSetup.remainingPercentage}%
                          </div>
                          <div className="small mt-1" style={{ color: C.red }}>
                            Remaining
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 3. Message (last): icon on top, title below, message below */}
                    <div
                      className="d-flex flex-column align-items-center justify-content-center text-center p-3"
                      style={{
                        background: setupStatus.bg,
                        borderRadius: 12,
                        width: 200,
                        flexShrink: 0,
                        alignSelf: "stretch",
                      }}
                    >
                      <div
                        className="d-flex align-items-center justify-content-center mb-2"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background: setupStatus.color,
                          color: "#fff",
                          fontSize: 20,
                        }}
                      >
                        <i className={setupStatus.icon} />
                      </div>
                      <div
                        className="fw-bold lh-sm fs-4 "
                        style={{ color: setupStatus.color }}
                      >
                        {setupStatus.title}
                      </div>
                      <div className="text-secondary small fs-6 lh-sm mt-1">
                        {setupStatus.message}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-xl-6">
                  <div className="bg-white p-3 h-100" style={cardStyle}>
                    <CardHeader
                      title="Pending Actions"
                      subtitle="Complete these items to finish your setup"
                    />

                    {data.pendingActions.length === 0 ? (
                      <div
                        className="p-2 fw-semibold small"
                        style={{
                          background: C.greenSoft,
                          color: C.green,
                          borderRadius: 8,
                        }}
                      >
                        All set. No pending actions.
                      </div>
                    ) : (
                      <div
                        className="d-flex flex-column gap-2"
                        style={{ maxHeight: 190, overflowY: "auto" }}
                      >
                        {data.pendingActions.map((a) => (
                          <div
                            key={a.type}
                            className="d-flex align-items-center justify-content-between gap-2 px-2 py-2 border-bottom"
                            // style={cardStyle}
                          >
                            <div>
                              <div
                                className="fw-semibold lh-sm"
                                style={{ color: C.dark, fontSize: 13 }}
                              >
                                {pendingTitle[a.type] || a.type}
                              </div>
                              <div
                                className="text-secondary lh-sm"
                                style={{ fontSize: 11 }}
                              >
                                {a.message}
                              </div>
                            </div>
                            <span
                              className="fw-semibold px-2 py-1 text-nowrap flex-shrink-0"
                              style={{
                                background: C.redSoft,
                                color: C.red,
                                borderRadius: 20,
                                fontSize: 11,
                              }}
                            >
                              {a.remainingPercentage}% left
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ===== Row 2: Device Summary + Setup Checklist ===== */}
              <div className="row g-3">
                {/* ---- Device Summary ---- */}
                <div className="col-12 col-xl-5">
                  <div className="bg-white p-3 h-100" style={cardStyle}>
                    <CardHeader
                      title="Device Summary"
                      subtitle="Overview of all devices in your premise"
                    />

                    <div className="row g-2 mb-2">
                      <div className="col-4">
                        <SummaryCard
                          tone="blue"
                          value={d.total}
                          label="Total Devices"
                        />
                      </div>
                      <div className="col-4">
                        <SummaryCard
                          tone="green"
                          value={d.sceneScheduleRequiredDevices}
                          label="Scene & Schedule"
                          sub="(Chips + Driver + Curtain)"
                        />
                      </div>
                      <div className="col-4">
                        <SummaryCard
                          tone="orange"
                          value={otherDevices}
                          label="Other Devices"
                          sub="(Blaster + Lock)"
                        />
                      </div>
                    </div>

                    <div className="d-flex flex-column gap-2">
                      <DeviceRow
                        title="Chips"
                        subtitle="Smart switch controllers"
                        total={d.chips.total}
                        boxes={sceneScheduleBoxes(d.chips)}
                      />

                      <DeviceRow
                        title="Air Driver"
                        subtitle="Driver controllers"
                        total={d.airDriver.total}
                        boxes={[
                          ...sceneScheduleBoxes(d.airDriver.driver),
                          {
                            title: "Groups",
                            tone: "purple",
                            items: [
                              {
                                value: d.airDriver.group.total,
                                label: "Groups",
                                sub: `Required: ${d.airDriver.group.requiredGroups}`,
                                color: "blue",
                              },
                            ],
                          },
                        ]}
                      />

                      <DeviceRow
                        title="Air Curtain"
                        subtitle="Air curtain controllers"
                        total={d.airCurtain.total}
                        boxes={sceneScheduleBoxes(d.airCurtain)}
                      />

                      <div className="row g-2">
                        <div className="col-12 col-md-6">
                          <DeviceRow
                            title="Blaster"
                            subtitle="IR blaster devices"
                            total={d.blaster.total}
                            note="No scene or schedule required."
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <DeviceRow
                            title="Lock"
                            subtitle="Smart locks"
                            total={d.lock.total}
                            note="No scene or schedule required."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ---- Setup Checklist ---- */}
                <div className="col-12 col-xl-7">
                  <div className="bg-white p-3 h-100" style={cardStyle}>
                    <CardHeader
                      title="Setup Checklist"
                      subtitle="Detailed progress for each setup category"
                    />

                    <div className="row g-2">
                      <div className="col-12 col-md-6">
                        <ChecklistCard
                          title="Device Setup"
                          subtitle="All required devices are installed"
                          item={data.setupChecklist.device}
                          statsTitle="Air Driver Groups"
                          stats={[
                            {
                              value:
                                data.setupChecklist.device.airDriverGroup
                                  .requiredGroups,
                              label: "Required",
                            },
                            {
                              value:
                                data.setupChecklist.device.airDriverGroup
                                  .totalGroups,
                              label: "Total",
                            },
                            {
                              value:
                                data.setupChecklist.device.airDriverGroup
                                  .pendingGroups,
                              label: "Pending",
                              color: C.red,
                            },
                          ]}
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <ChecklistCard
                          title="Switch Naming"
                          subtitle="Give proper names to all switches"
                          item={data.setupChecklist.naming}
                          statsTitle="Switches"
                          stats={[
                            {
                              value: data.setupChecklist.naming.totalSwitches,
                              label: "Total",
                            },
                            {
                              value: data.setupChecklist.naming.namedSwitches,
                              label: "Named",
                            },
                            {
                              value: data.setupChecklist.naming.pendingSwitches,
                              label: "Pending",
                              color: C.red,
                            },
                          ]}
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <ChecklistCard
                          title="Scene Setup"
                          subtitle="Create and configure scenes"
                          item={data.setupChecklist.scene}
                          statsTitle="Scenes"
                          stats={[
                            {
                              value: data.setupChecklist.scene.totalScenes,
                              label: "Total",
                            },
                            {
                              value: data.setupChecklist.scene.requiredScenes,
                              label: "Required",
                            },
                          ]}
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <ChecklistCard
                          title="Schedule Setup"
                          subtitle="Create and configure schedules"
                          item={data.setupChecklist.schedule}
                          statsTitle="Schedules"
                          stats={[
                            {
                              value:
                                data.setupChecklist.schedule.totalSchedules,
                              label: "Total",
                            },
                            {
                              value:
                                data.setupChecklist.schedule.requiredSchedules,
                              label: "Required",
                            },
                          ]}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default AccountSummary;
